import { faComment as faCommentRegular } from "@fortawesome/pro-regular-svg-icons/faComment";
import { faEye } from "@fortawesome/pro-regular-svg-icons/faEye";
import { faRightToBracket } from "@fortawesome/pro-regular-svg-icons/faRightToBracket";
import { faShareNodes } from "@fortawesome/pro-regular-svg-icons/faShareNodes";
import { faStar as faStarRegular } from "@fortawesome/pro-regular-svg-icons/faStar";
import { faThumbsUp, faThumbsUp as faThumbsUpRegular } from "@fortawesome/pro-regular-svg-icons/faThumbsUp";
import { faComment as faCommentSolid } from "@fortawesome/pro-solid-svg-icons/faComment";
import { faStar as faStarSolid } from "@fortawesome/pro-solid-svg-icons/faStar";
import { faThumbsUp as faThumbsUpSolid } from "@fortawesome/pro-solid-svg-icons/faThumbsUp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, IconButton, Typography } from "@mui/material";
import { fetchKnowledgeQuickAccess } from "app/features/menu/menuSlice";
import { store } from "app/store";
import { MprcButton, MprcIconButtonDropdown } from "components";
import { IconFollow } from "components/icons";
import { MprcDropdownBuildGroupedItems, MprcDropdownItem } from "components/MprcIconButtonDropdown";
import { ExtractErrorInformation, MprcRequestError, MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { MprcSimpleEditDialog } from "components/MprcSimpleEditDialog";
import { MemberType, PublicationType } from "generated/CoreApi_v1";
import HtmlUtils from "lib/HtmlUtils";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import React, { startTransition, useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { useCardContext } from "views/knowledge/components/card/CardBuilder";
import { CommentDialog } from "views/knowledge/components/comment/CommentDialog";
import { AddToListDrawer } from "../../actions/AddToListDrawer";
import { RecommendPublicationDialog } from "../../actions/RecommendPublicationDialog";
import { SharePublicationDialog } from "../../actions/SharePublicationDialog";
import { WhoLikesDrawer } from "../../actions/WhoLikesDrawer";
import { WhoViewDrawer } from "../../actions/WhoViewDrawer";

///
export const LikeButton = () => {
  const { pub: { publicationId, title, interaction, otherInfo, readOnly, author }, onFeedItemChanged } = useCardContext();

  const session = useSession();
  const api = useCoreApi();
  const { t } = useTranslation("knowledge");
  const { enqueueSnackbar } = useSnackbar();
  const [updating, setUpdating] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);

  const addOrRemoveLike = () => {
    setUpdating(true);
    api.v1.knowledgeFeed.addOrRemoveLike({ publicationId })
      .then((result) => {
        onFeedItemChanged({
          interaction: { ...interaction, hasLiked: !interaction.hasLiked },
          otherInfo: { ...otherInfo, likeCount: result.data }
        });
      })
      .catch((e) => {
        let error = ExtractErrorInformation(e as MprcRequestError);
        enqueueSnackbar(error.message, { variant: "error" });
      })
      .finally(() => setUpdating(false));
  };

  const ownUser = author.userId == session.user.userId;
  const disabled = updating || (ownUser && otherInfo.likeCount == 0) || (!ownUser && readOnly);

  return (
    <>
      <Typography
        variant={interaction.hasLiked ? "iconTextHighlight" : "iconText"}
        component="div"
        title={
          ownUser
            ? otherInfo.likeCount == 0 ? t("Feed.Card.Options.LikeNotAllowed_zero") : t("Feed.Card.Options.LikeNotAllowed_other")
            : disabled
              ? t("Feed.Card.Options.LikeTotal")
              : interaction.hasLiked
                ? t("Feed.Card.Options.Unlike")
                : t("Feed.Card.Options.AddLike")}
      >
        <IconButton
          color="inherit"
          disabled={disabled}
          onClick={ownUser ? () => setOpen(true) : addOrRemoveLike}
        >
          <FontAwesomeIcon
            icon={interaction.hasLiked ? faThumbsUpSolid : faThumbsUpRegular}
            fixedWidth
          />
        </IconButton>
        <span>{otherInfo.likeCount}</span>
      </Typography>

      {open &&
        <WhoLikesDrawer
          publicationId={publicationId}
          title={title}
          onClose={() => setOpen(false)}
        />
      }
    </>
  );
};

///
export const CommentButton = () => {
  const { pub: { interaction, otherInfo, title, publicationId, readOnly }, onFeedItemChanged } = useCardContext();

  const { t } = useTranslation("knowledge");
  const [dialog, setDialog] = useState<React.ReactNode | null>(null);
  const total = useRef<number>(otherInfo.commentCount);

  const doComment = () => {
    setDialog(
      <CommentDialog
        publicationTitle={title}
        publicationId={publicationId}
        readOnly={readOnly}
        onClose={() => {
          onFeedItemChanged({
            interaction: { ...interaction, hasCommented: total.current > otherInfo.commentCount ? true : interaction.hasCommented },
            otherInfo: { ...otherInfo, commentCount: total.current }
          });
          setDialog(null);
        }}
        onUpdateCommentCounter={(totalComments) => total.current = totalComments}
      />
    );
  };

  return (
    <>
      {dialog}

      <Typography
        variant={interaction.hasCommented ? "iconTextHighlight" : "iconText"}
        component="div"
        title={t("Feed.Card.Options.AddComment")}
      >
        <IconButton color="inherit" onClick={doComment}>
          <FontAwesomeIcon icon={interaction.hasCommented ? faCommentSolid : faCommentRegular} fixedWidth />
        </IconButton>
        <span>{otherInfo.commentCount}</span>
      </Typography>
    </>
  );
};

type FavoriteButtonOptions = "favorites" | "seeLater" | "newList" | "removeFromLists" | "selectLists";

///
export const FavoriteButton = ({ showLabel = false }: { showLabel?: boolean; }) => {
  const { pub, onFeedItemChanged, pub: { title, interaction, readOnly } } = useCardContext();
  const api = useCoreApi();
  const { enqueueSnackbar } = useSnackbar();
  const [dialog, setDialog] = useState<any>();
  const { t } = useTranslation("knowledge");

  const menuOptions = useMemo(() => {
    var result: MprcDropdownItem<FavoriteButtonOptions>[] = [];

    if (interaction.hasFavorited) {
      result = [
        { text: t("Feed.Card.Common.Favorites.RemoveFromListsText"), value: "removeFromLists" },
        { text: t("Feed.Card.Common.Favorites.SelectListsText"), value: "selectLists" },
      ];
    }
    else {
      result = MprcDropdownBuildGroupedItems(
        [
          [
            { text: t("Feed.Card.Common.Favorites.FavoritesText"), value: "favorites" },
            { text: t("Feed.Card.Common.Favorites.SeeLateText"), value: "seeLater" }
          ],
          [
            { text: t("Feed.Card.Common.Favorites.SelectListsText"), value: "selectLists" },
            { text: t("Feed.Card.Common.Favorites.NewListText"), value: "newList" }
          ]
        ]
      );
    }

    return result;
  }, [interaction.hasFavorited]);

  ///
  const addToFavorites = useCallback(() => {
    startTransition(() => {
      api.v1.knowledgePublication.addToListFavorites({ publicationId: pub.publicationId })
        .then((result) => {
          if (result.data) {
            enqueueSnackbar(t("Feed.Card.Actions.Favorites.AddToFavoritesSuccessfull"), { variant: "success" });
            onFeedItemChanged({ interaction: { ...interaction, hasFavorited: true } });
          }
          else {
            enqueueSnackbar(t("Feed.Card.Actions.Favorites.AddToListFailed"));
          }
        })
        .catch(e => {
          MprcRequestSnackbarErrorReport(e);
        });
    });
  }, [interaction.hasFavorited]);

  const addToSeelater = useCallback(() => {
    startTransition(() => {
      api.v1.knowledgePublication.addToListSeeLater({ publicationId: pub.publicationId })
        .then((result) => {
          if (result.data) {
            enqueueSnackbar(t("Feed.Card.Actions.Favorites.AddToSeeLaterSuccessfull"), { variant: "success" });
            onFeedItemChanged({ interaction: { ...interaction, hasFavorited: true } });
          }
          else {
            enqueueSnackbar(t("Feed.Card.Actions.Favorites.AddToListFailed"));
          }
        })
        .catch(e => {
          MprcRequestSnackbarErrorReport(e);
        });
    });
  }, [interaction.hasFavorited]);

  const removeFromLists = useCallback(() => {
    startTransition(() => {
      api.v1.knowledgePublication.removeItemFromLists({ itemPublicationId: pub.publicationId })
        .then((result) => {
          if (result.data) {
            enqueueSnackbar(t("Feed.Card.Actions.Favorites.RemoveFromListsSuccessfull"), { variant: "success" });
            onFeedItemChanged({ interaction: { ...interaction, hasFavorited: false } });
          }
          else {
            enqueueSnackbar(t("Feed.Card.Actions.Favorites.RemoveFromListsFailed"));
          }
        })
        .catch(e => {
          MprcRequestSnackbarErrorReport(e);
        });
    });

  }, [interaction.hasFavorited]);

  ///
  const handleMenuItem = (option: FavoriteButtonOptions) => {
    switch (option) {
      case "favorites":
        addToFavorites();
        break;
      case "seeLater":
        addToSeelater();
        break;
      case "removeFromLists":
        removeFromLists();
        break;
      case "newList":
        setDialog(
          <MprcSimpleEditDialog
            title={t("Feed.Card.Actions.Favorites.CreateListTitle")}
            placeholder={t("Feed.Card.Actions.AddToListDrawer.AddFocusedPlaceholder")}
            acceptEmptyValue={false}
            onConfirm={async (name) => {
              if (name) {
                startTransition(() => {
                  api.v1.knowledgePublication.createPrivateList({ itemId: pub.publicationId, title: name })
                    .then((result) => {
                      if (result.data.success) {
                        enqueueSnackbar(t("Feed.Card.Actions.AddToListDrawer.AddNewListMessage", { name: name }), { variant: "success" });
                        onFeedItemChanged({ interaction: { ...interaction, hasFavorited: true } });
                      }
                      else {
                        enqueueSnackbar(t("Feed.Card.Actions.AddToListDrawer.CreateListErrorMessage"));
                      }
                    })
                    .catch(e => {
                      MprcRequestSnackbarErrorReport(e);
                    });
                });
              }
            }}
            onClose={() => {
              setDialog(null);
            }}
          />
        );
        break;
      case "selectLists":
        setDialog(
          <AddToListDrawer
            publicationId={pub.publicationId}
            onChanged={(items) => onFeedItemChanged({ interaction: { ...interaction, hasFavorited: items.filter(p => p.selected).length > 0 } })}
            subtitle={title}
            onClose={() => setDialog(null)}
          />
        );
        break;
    }
  };

  return !interaction.allowAddLists
    ? null
    : (
      <>
        {dialog}
        <Typography variant={interaction.hasFavorited ? "iconTextHighlight" : "iconText"} component="div">
          <MprcIconButtonDropdown
            text={showLabel ? t("Feed.Card.Common.SaveFavoriteLabel") : undefined}
            icon={<FontAwesomeIcon icon={interaction.hasFavorited ? faStarSolid : faStarRegular} fixedWidth />}
            items={menuOptions}
            onChange={handleMenuItem}
            separator="blank"
            disabled={readOnly}
            title={t("Feed.Card.Common.SaveFavoriteTooltip")}
          />
        </Typography>

      </>
    );
};

///
type ShareButtonOptions = "recommendToUsers" | "recommendToGroups" | "share" | "copyLink";

export const ShareButton = () => {

  const { pub: { publicationId, publicationType, title, readOnly, interaction: { allowShare } } } = useCardContext();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation("knowledge");

  const [dialog, setDialog] = useState<any>();

  ///
  const copyLink = () => {
    const host = window.location.protocol + "//" + window.location.host;
    const publicationUrl = `${host}/Knowledge/${PublicationType[publicationType].toString()}/${publicationId}`;

    HtmlUtils.copyToClipboard(publicationUrl)
      .then(() => enqueueSnackbar(t("Feed.Card.Common.CopyLinkSuccessMessage"), { variant: "success" }))
      .catch(() => enqueueSnackbar(t("Feed.Card.Common.CopyLinkFailMessage"), { variant: "error" }));
  };

  ///
  const getMenuItems = (): MprcDropdownItem<ShareButtonOptions>[] => {
    var group0: MprcDropdownItem<ShareButtonOptions>[] = [
      { text: t("Feed.Card.Common.RecommendToUsersAction"), value: "recommendToUsers" },
      { text: t("Feed.Card.Common.RecommendToGroupsAction"), value: "recommendToGroups" },
      { text: t("Feed.Card.Common.ShareOnChannelAction"), value: "share" }

    ];
    var group1: MprcDropdownItem<ShareButtonOptions>[] = [
      { text: t("Feed.Card.Common.CopyLinkAction"), value: "copyLink" }

    ];

    return MprcDropdownBuildGroupedItems([group0, group1]);
  };

  ///
  const handleMenuItem = (option: ShareButtonOptions) => {
    switch (option) {
      case "recommendToUsers":
        setDialog(
          <RecommendPublicationDialog
            publicationId={publicationId}
            title={title}
            memberType={MemberType.User}
            onClose={() => setDialog(null)}
          />
        );
        break;
      case "recommendToGroups":
        setDialog(
          <RecommendPublicationDialog
            publicationId={publicationId}
            title={title}
            memberType={MemberType.Group}
            onClose={() => setDialog(null)}
          />
        );
        break;
      case "share":
        setDialog(
          <SharePublicationDialog
            publicationId={publicationId}
            title={title}
            onClose={() => setDialog(null)}
          />
        );
        break;
      case "copyLink":
        copyLink();
        break;
    }
  };

  if (!allowShare) return <></>;

  return (
    <>
      {dialog}
      <Typography variant="iconText" component="div">
        <MprcIconButtonDropdown
          icon={<FontAwesomeIcon icon={faShareNodes} fixedWidth />}
          items={getMenuItems()}
          onChange={handleMenuItem}
          separator="blank"
          disabled={readOnly}
          title={t("Feed.Card.Common.ShareButtonTooltip")}
        />
      </Typography>
    </>
  );
};

///
export const FollowButton = () => {
  const { pub: { publicationId, publicationType, interaction, readOnly }, onFeedItemChanged } = useCardContext();
  const api = useCoreApi();
  const { t } = useTranslation("knowledge");

  const [updating, setUpdating] = useState<boolean>(false);

  const handleFollow = () => {
    setUpdating(true);
    api.v1.knowledgeSubcription.subscribePublication({ publicationId })
      .then((result) => {
        if (result) {
          store.dispatch(fetchKnowledgeQuickAccess({}));
          onFeedItemChanged({
            interaction: { ...interaction, followingPublication: true },
          });
        }
      })
      .catch((e) => { })
      .finally(() => setUpdating(false));
  };

  const handleUnfollow = () => {
    setUpdating(true);
    api.v1.knowledgeSubcription.unsubscribePublication({ publicationId })
      .then((result) => {
        if (result) {
          store.dispatch(fetchKnowledgeQuickAccess({}));
          onFeedItemChanged({
            interaction: { ...interaction, followingPublication: false },
          });
        }
      })
      .catch((e) => { })
      .finally(() => setUpdating(false));
  };

  const text = interaction.followingPublication
    ? publicationType == PublicationType.Question
      ? t("Feed.Card.Question.FollowingButtonText")
      : t("Feed.Card.Common.FollowingButtonText")
    : publicationType == PublicationType.Question
      ? t("Feed.Card.Question.FollowButtonText")
      : t("Feed.Card.Common.FollowButtonText");

  const title = interaction.followingPublication
    ? publicationType == PublicationType.Question
      ? t("Feed.Card.Question.FollowingButtonTitle")
      : t("Feed.Card.Common.FollowingButtonTitle")
    : publicationType == PublicationType.Question
      ? t("Feed.Card.Question.FollowButtonTitle")
      : t("Feed.Card.Common.FollowButtonTitle");

  return (
    <MprcButton
      text={text}
      title={title}
      disabled={updating || readOnly}
      color={
        interaction.followingPublication
          ? "highlight"
          : "muted"
      }
      startIcon={<IconFollow />}
      onClick={
        interaction.followingPublication
          ? handleUnfollow
          : handleFollow
      }
    />
  );
};

///
export const ViewMoreButton = () => {
  const { pub } = useCardContext();

  const { t } = useTranslation("knowledge");
  const navigate = useNavigate();

  return (
    <Typography variant="iconTextHighlight" component="div">
      <Button
        color="inherit"
        onClick={() => navigate(KnowledgePaths.PublicationViewerResolver(pub.publicationId, pub.publicationType, pub.masterPublication?.publicationId))}
      >
        <FontAwesomeIcon icon={faRightToBracket} fixedWidth />
        {t("Feed.Card.Common.ViewMoreButtonText")}
      </Button>
    </Typography>
  );
};


///
export const WhoLikesButton = () => {
  const { pub: { publicationId, title, otherInfo } } = useCardContext();
  const { t } = useTranslation("knowledge");

  const [dialog, setDialog] = useState<any>();

  const viewLikes = () => {
    setDialog(
      <WhoLikesDrawer
        publicationId={publicationId}
        title={title}
        onClose={() => setDialog(null)}
      />
    );
  };

  return (
    <>
      {dialog}
      <Typography variant={"iconText"} component="div">
        <IconButton color="inherit" onClick={viewLikes} title={t("Feed.PublicationAdmin.WhoLikeButtonTooltip")}>
          <FontAwesomeIcon icon={faThumbsUp} fixedWidth />
        </IconButton>
        <span>{otherInfo.likeCount}</span>
      </Typography>
    </>
  );
};

///
export const WhoViewButton = () => {
  const { pub: { publicationId, title, otherInfo } } = useCardContext();
  const { t } = useTranslation("knowledge");

  const [dialog, setDialog] = useState<any>();

  const viewLikes = () => {
    setDialog(
      <WhoViewDrawer
        publicationId={publicationId}
        title={title}
        onClose={() => setDialog(null)}
      />
    );
  };

  return (
    <>
      {dialog}
      <Typography variant={"iconText"} component="div">
        <IconButton color="inherit" onClick={viewLikes} title={t("Feed.PublicationAdmin.WhoViewButtonTooltip")}>
          <FontAwesomeIcon icon={faEye} fixedWidth />
        </IconButton>
        <span>{otherInfo.viewCount}</span>
      </Typography>
    </>
  );
};
